import QueueWidgetGroup from "@/components/widgets/call-overview/QueueWidgetGroup";
import QueuePerformanceTableWidget from "@/components/widgets/call-overview/QueuePerformanceTableWidget";
import InboundCallStatusDistributionWidget from "@/components/widgets/call-overview/InboundCallStatusDistributionWidget";
import CallsHandledByQueueWidget from "@/components/widgets/call-overview/CallsHandledByQueueWidget";
import CallVolumeWidget from "@/components/widgets/call-overview/CallVolumeWidget";
import { fetchQueueOverviewWidgetsBatch, InboundCallStatusDistributionData } from "@/lib/axios/api/widgets.api";
import { InboundHandledByQueueRow } from "@/lib/axios/types/widgets.type";
import { CallQueueType } from "@/constants/queue";
import { SectionType } from "@/constants/section";
import { useAppData, UserRole } from "@/contexts/AppDataContext";
import { useDashboardData } from "@/contexts/DashboardContext";
import { useFilterCache } from "@/hooks/useFilterCache";
import { DateRangeChange, FilterChangeEvent } from "@/types";
import { calculateDateRange, DATE_RANGE_KEYS, getRecalculatedDatesIfNeeded } from '@/utils/dateRangeUtils';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FilterBar from "../../common/FilterBar";
import StickyFilterBar from "../../common/StickyFilterBar";
import { cn } from "@/lib/utils";

const QUEUE_LIST_PAGE_SIZE = 2;

interface CallOverviewWidgetsProps {
  isGeneralMode: boolean;
  filterClass?: string;
}

function buildQueueCallVolumeRows(startTime: string, intervalMinutes: number, inboundValues: number[]) {
  const start = new Date(startTime);
  return inboundValues.map((inboundCall, index) => ({
    time: new Date(start.getTime() + index * intervalMinutes * 60_000),
    inboundCall,
    outboundCall: 0,
  }));
}

function getAllQueuesLoadedText(selectedQueuesCount: number): string | null {
  return selectedQueuesCount > 0 ? `All ${selectedQueuesCount} queues loaded` : null;
}

/**
 * Single source of truth for resolving a filter selection (manual or "all") into the
 * queueMicrosoftIds/groupIds actually used to fetch data. Shared by mount, manual filter
 * changes, and the role/queue-change effect below so all three stay in sync — a manual
 * selection is always clamped to the caller's *current* authorized queueMicrosoftIds, never
 * trusted verbatim, so a queue the manager has since lost access to can't leak through.
 */
function resolveScopedIds(
  selectedQueueType: CallQueueType | undefined,
  rawSelectedQueueIds: string[],
  queueMicrosoftIds: string[],
  directAgentGroupIds: number[],
  userRole: UserRole,
): { queueMicrosoftIds: string[] | null; groupIds: number[] | null } {
  if (selectedQueueType === CallQueueType.DIRECT_AGENT_GROUP) {
    return {
      queueMicrosoftIds: null,
      groupIds: directAgentGroupIds.filter(
        id => rawSelectedQueueIds.includes(id.toString()) || rawSelectedQueueIds.length === 0
      ),
    };
  }
  const authorized = new Set(queueMicrosoftIds);
  const clamped = rawSelectedQueueIds.filter(id => authorized.has(id));

  let resolvedQueueIds: string[] | null;
  if (userRole === UserRole.ADMIN && clamped.length === 0) {
    resolvedQueueIds = null;
  } else if (clamped.length > 0) {
    resolvedQueueIds = clamped;
  } else {
    resolvedQueueIds = queueMicrosoftIds;
  }
  return { queueMicrosoftIds: resolvedQueueIds, groupIds: null };
}


const CallOverviewSection: React.FC<CallOverviewWidgetsProps> = ({ isGeneralMode, filterClass }) => {
  const defaultDateRangeKey = DATE_RANGE_KEYS.LAST_7_DAYS;
  const { current } = useDashboardData();
  const { filters, saveFilters } = useFilterCache("call-overview", current?.id);
  const [dateRangeKey, setDateRangeKey] = React.useState<string>(filters?.dateRangeKey ?? defaultDateRangeKey);
  const [dateRange, setDateRange] = React.useState<DateRangeChange>({
    key: filters?.dateRangeKey ?? defaultDateRangeKey,
    startDate: filters?.startDate ?? null,
    endDate: filters?.endDate ?? null,
    previousStartDate: filters?.previousStartDate ?? null,
    previousEndDate: filters?.previousEndDate ?? null,
  });
  const { directAgentGroupIds, queueMicrosoftIds, userRole, queues, queuesDataVersion, loaded: appDataLoaded } = useAppData();

  // State for date range filter
  const [dateParams, setDateParams] = React.useState(() => {
    if (filters) {
      const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(
        filters.dateRangeKey,
        filters
      );

      setDateRange({
        key: filters?.dateRangeKey,
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
      });
      const scoped = resolveScopedIds(
        filters.selectedQueueType, filters.queues ?? [], queueMicrosoftIds, directAgentGroupIds, userRole
      );
      return {
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
        queueMicrosoftIds: scoped.queueMicrosoftIds,
        agentMicrosoftIds: filters.agents?.length ? filters.agents : null,
        groupIds: scoped.groupIds,
        timeStamp: new Date().toISOString()
      };
    }

    const dateRange = calculateDateRange(defaultDateRangeKey);
    const scoped = resolveScopedIds(undefined, [], queueMicrosoftIds, directAgentGroupIds, userRole);
    return {
      startDate: dateRange.from.toISOString(),
      endDate: dateRange.to.toISOString(),
      previousStartDate: dateRange.previousFrom.toISOString(),
      previousEndDate: dateRange.previousTo.toISOString(),
      queueMicrosoftIds: scoped.queueMicrosoftIds,
      agentMicrosoftIds: null,
      groupIds: scoped.groupIds,
      timeStamp: new Date().toISOString()
    };
  });

  const handleFilterChange = async (event: FilterChangeEvent) => {
    const rawQueues = event.queues || [];
    setDateRangeKey(event.dateRange.key);

    // Recalculate dates for dynamic date ranges to ensure freshness
    const recalculatedDates = getRecalculatedDatesIfNeeded(event.dateRange.key, {
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
    });

    setDateRange({
      key: event.dateRange.key,
      startDate: recalculatedDates.startDate,
      endDate: recalculatedDates.endDate,
      previousStartDate: recalculatedDates.previousStartDate,
      previousEndDate: recalculatedDates.previousEndDate,
    });
    const scoped = resolveScopedIds(
      event.selectedQueueType, rawQueues, queueMicrosoftIds, directAgentGroupIds, userRole
    );
    setDateParams(prev => ({
      ...prev,
      startDate: recalculatedDates.startDate,
      endDate: recalculatedDates.endDate,
      previousStartDate: recalculatedDates.previousStartDate,
      previousEndDate: recalculatedDates.previousEndDate,
      queueMicrosoftIds: scoped.queueMicrosoftIds,
      agentMicrosoftIds: null, // disable agent filter for call overview
      groupIds: scoped.groupIds,
      timeStamp: new Date().toISOString()
    }));

    // persist
    saveFilters({
      dateRangeKey: event.dateRange.key,
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
      queues: event.queues ?? [],
      agents: event.agents ?? [],
      selectedQueueType: event.selectedQueueType,
    });
  };

  // Refresh data when tab becomes active, when with-agents-and-role poll detects a real change,
  // or when the *first* with-agents-and-role fetch finishes loading (skip initial mount —
  // dateParams already has a fresh timeStamp). That first-load case matters on its own:
  // `queuesDataVersion` deliberately does NOT bump for the initial fetch (see AppDataContext),
  // so if this component mounts (and clamps a persisted filter selection) before that fetch
  // resolves, queueMicrosoftIds/directAgentGroupIds are still empty and nothing would otherwise
  // ever recompute dateParams against the real scope once it arrives — the table would be stuck
  // clamped to nothing. `appDataLoaded` flipping false -> true is exactly that missing signal.
  const isFirstActiveRef = React.useRef(true);
  React.useEffect(() => {
    if (isFirstActiveRef.current) {
      isFirstActiveRef.current = false;
      return;
    }
    if (isGeneralMode) {
      const scoped = resolveScopedIds(
        filters?.selectedQueueType, filters?.queues ?? [], queueMicrosoftIds, directAgentGroupIds, userRole
      );
      setDateParams(prev => ({
        ...prev,
        queueMicrosoftIds: scoped.queueMicrosoftIds,
        groupIds: scoped.groupIds,
        timeStamp: new Date().toISOString()
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed only on
    // isGeneralMode/queuesDataVersion/appDataLoaded (the "something real changed" signals);
    // filters/queueMicrosoftIds/directAgentGroupIds/userRole are read fresh from closure each
    // time this fires. Listing them as deps would also re-fire this effect right after
    // handleFilterChange updates `filters` via saveFilters — redundant (same result) but bumps
    // timeStamp again and double-fetches every widget for no reason.
  }, [isGeneralMode, queuesDataVersion, appDataLoaded]);

  // TCX-6287 Queue tab: one set of 6 widgets per Call Queue, revealed 5-at-a-time as the user
  // scrolls (mirrors teamscx-realtime-report's Queue tab lazy load). The full queue list is
  // already available client-side via useAppData(), so "loading more" means widening the
  // rendered slice — no extra /queues network round-trip is needed.
  const selectedQueues = useMemo(() => {
    const ids = dateParams.queueMicrosoftIds;
    if (!ids) return queues;
    const idSet = new Set(ids);
    return queues.filter(q => idSet.has(q.microsoftId));
  }, [queues, dateParams.queueMicrosoftIds]);

  const [visibleCount, setVisibleCount] = useState(QUEUE_LIST_PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const selectedQueueIdsKey = useMemo(() => selectedQueues.map(q => q.microsoftId).join(","), [selectedQueues]);

  useEffect(() => {
    setVisibleCount(QUEUE_LIST_PAGE_SIZE);
  }, [selectedQueueIdsKey]);

  const canLoadMore = visibleCount < selectedQueues.length;

  // Only reveal more queues once the user has actually scrolled the tab's scroll container —
  // otherwise a short queue list leaves the sentinel inside the viewport on mount and the
  // observer fires immediately, bursting through every batch with no real lazy load.
  const userScrolledRef = useRef(false);
  useEffect(() => {
    const root = document.querySelector(".overflow-auto");
    if (!root) return;
    const onScroll = () => {
      userScrolledRef.current = true;
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    return () => root.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!canLoadMore) return;
    const el = sentinelRef.current;
    const root = document.querySelector(".overflow-auto");
    if (!el || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (!userScrolledRef.current) return;
        setVisibleCount(prev => Math.min(selectedQueues.length, prev + QUEUE_LIST_PAGE_SIZE));
      },
      { root, rootMargin: "160px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [canLoadMore, selectedQueues.length]);

  const visibleQueues = selectedQueues.slice(0, visibleCount);
  const sentinelText = canLoadMore
    ? "Loading queues…"
    : getAllQueuesLoadedText(selectedQueues.length);

  // Queue Performance table row click -> jump to that queue's widget group below. If the
  // queue is beyond the current lazy-load slice, widen the slice first (loading whatever
  // queues sit ahead of it too, same as normal scroll-triggered pagination) then scroll once
  // its wrapper div has mounted.
  const queueGroupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [scrollTargetId, setScrollTargetId] = useState<string | null>(null);
  // Brief background flash on the target group once scrolled to, so the user's eye lands on
  // the right widget instead of having to compare 5 identical-looking sections at a glance.
  const [highlightId, setHighlightId] = useState<string | null>(null);

  const handleQueueRowClick = useCallback((queueName: string) => {
    const idx = selectedQueues.findIndex(q => q.label === queueName);
    if (idx === -1) return;
    if (idx + 1 > visibleCount) {
      setVisibleCount(Math.min(selectedQueues.length, idx + 1));
    }
    setScrollTargetId(selectedQueues[idx].microsoftId);
  }, [selectedQueues, visibleCount]);

  useEffect(() => {
    if (!scrollTargetId) return;
    const el = queueGroupRefs.current[scrollTargetId];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(scrollTargetId);
    setScrollTargetId(null);
  }, [scrollTargetId, visibleCount]);

  // Separate from the scroll effect above: that effect re-runs the instant scrollTargetId
  // flips back to null, which would clear this timer via its own cleanup before it ever fires.
  useEffect(() => {
    if (!highlightId) return;
    const timer = setTimeout(() => setHighlightId(null), 1000);
    return () => clearTimeout(timer);
  }, [highlightId]);

  // Tab-level overview widgets (TCX-6287): rendered once per tab, scoped to the whole
  // selection, instead of once per queue.
  const { startDate, endDate, timeStamp, queueMicrosoftIds: overviewQueueMicrosoftIds } = dateParams;

  // Falls back to the full queueMicrosoftIds from context when dateParams hasn't got an
  // explicit selection yet (e.g. admin's null on first load) — same resolution CallVolumeSeries
  // (widget 4) already relies on to get all queues on first request.
  const resolvedQueueMicrosoftIds = useMemo(() => {
    if (dateParams.groupIds) return [];
    return dateParams.queueMicrosoftIds ?? queueMicrosoftIds;
  }, [dateParams.groupIds, dateParams.queueMicrosoftIds, queueMicrosoftIds]);

  // Stable value-based keys for the widgets-fetch effect below: `resolvedQueueMicrosoftIds`
  // is a new array reference on every recompute (e.g. context's queueMicrosoftIds churns on
  // each 5s filter poll even when its contents are unchanged), which would otherwise re-fire
  // the /queue/widgets fetch every poll tick instead of only when the actual id set changes.
  const resolvedQueueMicrosoftIdsKey = useMemo(
    () => [...resolvedQueueMicrosoftIds].sort((a, b) => a.localeCompare(b)).join(","),
    [resolvedQueueMicrosoftIds]
  );
  const overviewQueueMicrosoftIdsKey = useMemo(
    () => [...(overviewQueueMicrosoftIds ?? [])].sort((a, b) => a.localeCompare(b)).join(","),
    [overviewQueueMicrosoftIds]
  );

  const [inboundDistributionData, setInboundDistributionData] = useState<InboundCallStatusDistributionData | null>(null);
  const [inboundDistributionLoading, setInboundDistributionLoading] = useState(true);
  const [inboundDistributionError, setInboundDistributionError] = useState<string | null>(null);

  const [queueHandledData, setQueueHandledData] = useState<InboundHandledByQueueRow[]>([]);
  const [queueHandledLoading, setQueueHandledLoading] = useState(false);
  const [queueHandledError, setQueueHandledError] = useState(false);

  const [callVolumeData, setCallVolumeData] = React.useState<Array<{ time: Date; inboundCall: number; outboundCall: number }>>([]);
  const [callVolumeLoading, setCallVolumeLoading] = React.useState(false);
  const [callVolumeError, setCallVolumeError] = React.useState<string>();

  // One batched POST /queue/widgets per refresh cycle for the 3 tab-level widgets (widgetId 2,
  // 3, 4) instead of one request each; the response fans back out to per-widget state below.
  useEffect(() => {
    if (!startDate || !endDate) return;
    // Backend rejects widgetId 3/4 with 400 when queueMicrosoftIds is empty; only
    // InboundHandledByQueue (widgetId 2) is exempt. See ReportEndpoints.cs. The batch fetch
    // omits both items in that case, so don't surface a loading state for Call Volume.
    const hasQueueScopedWidgets = resolvedQueueMicrosoftIds.length > 0;
    const controller = new AbortController();
    setQueueHandledLoading(true);
    setQueueHandledError(false);
    setInboundDistributionLoading(true);
    setInboundDistributionError(null);
    setCallVolumeLoading(hasQueueScopedWidgets);
    setCallVolumeError(undefined);
    if (!hasQueueScopedWidgets) setCallVolumeData([]);

    fetchQueueOverviewWidgetsBatch(
      {
        startDate,
        endDate,
        handledQueueMicrosoftIds: overviewQueueMicrosoftIds,
        queueMicrosoftIds: resolvedQueueMicrosoftIds,
      },
      { signal: controller.signal }
    )
      .then(({ handledByQueue, inboundDistribution, callVolumeSeries }) => {
        setQueueHandledData(handledByQueue);
        setInboundDistributionData(inboundDistribution);
        if (callVolumeSeries) {
          const inboundValues = callVolumeSeries.series.find(series => series.key === "Total")?.values ?? [];
          setCallVolumeData(buildQueueCallVolumeRows(startDate, callVolumeSeries.intervalMinutes, inboundValues));
        }
      })
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setQueueHandledError(true);
        setInboundDistributionError("Failed to load inbound call status distribution");
        if (hasQueueScopedWidgets) setCallVolumeError("Failed to load");
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setQueueHandledLoading(false);
        setInboundDistributionLoading(false);
        setCallVolumeLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the stable string forms
    // above; resolvedQueueMicrosoftIds/overviewQueueMicrosoftIds are read from closure and
    // always match the key's render, but including the arrays themselves would defeat the key.
  }, [startDate, endDate, timeStamp, resolvedQueueMicrosoftIdsKey, overviewQueueMicrosoftIdsKey]);

  return (
    <div className="w-full">
      <StickyFilterBar className={filterClass}>
        <FilterBar
          onFilterChange={handleFilterChange}
          initialDateRangeKey={dateRangeKey}
          initialSelectedQueues={filters?.queues}
          initialSelectedAgents={filters?.agents}
          initialSelectedQueueType={filters?.selectedQueueType}
          initialDateRange={dateRange}
          sectionType={SectionType.CALL_OVERVIEW}
        />
      </StickyFilterBar>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch mb-4">
        <div className="flex flex-col gap-4 lg:col-span-1">
          <InboundCallStatusDistributionWidget
            loading={inboundDistributionLoading}
            error={inboundDistributionError}
            data={inboundDistributionData}
          />
          <CallsHandledByQueueWidget
            loading={queueHandledLoading}
            error={queueHandledError ? "Failed to load" : undefined}
            data={queueHandledData}
          />
        </div>
        <div className="lg:col-span-2 flex">
          <CallVolumeWidget
            loading={callVolumeLoading}
            error={callVolumeError}
            data={callVolumeData}
            dateRangeType={dateRangeKey}
            dateRange={{ from: new Date(startDate), to: new Date(endDate) }}
          />
        </div>
      </div>

      <div className="mb-4">
        <QueuePerformanceTableWidget
          startDate={startDate}
          endDate={endDate}
          queueMicrosoftIds={overviewQueueMicrosoftIds}
          onQueueClick={handleQueueRowClick}
          queuesDataVersion={queuesDataVersion}
        />
      </div>

      <div className="flex flex-col gap-4">
        {visibleQueues.map((q) => (
          <div
            key={q.microsoftId}
            ref={(el) => { queueGroupRefs.current[q.microsoftId] = el; }}
            className={cn(
              "rounded-lg ring-0 ring-[#00FEC1]/50 transition-all duration-700 ease-out",
              highlightId === q.microsoftId && "ring-1"
            )}
          >
            <QueueWidgetGroup
              queueMicrosoftId={q.microsoftId}
              queueName={q.label}
              dateParams={dateParams}
              queuesDataVersion={queuesDataVersion}
            />
          </div>
        ))}
      </div>

      <div
        ref={sentinelRef}
        className="flex min-h-[40px] items-center justify-center py-3 text-xs text-muted-foreground dark:text-white/70"
      >
        {sentinelText}
      </div>
    </div>
  );
};

export default CallOverviewSection;
